/**
 * Work Order and related types for Production Planning
 * (Formerly Manufacturing Order - renamed to work for both manufacturing and distribution)
 */

export interface WorkOrder {
	id: number;
	productId?: number;
	productName?: string;
	productItemNumber?: string;
	sku?: string;
	quantity: number;
	/**
	 * The work center this STEP runs on. A work order is one operation of a
	 * standard on one work center for a planned window. The server sends null
	 * for a BATCH HEADER (a parent with no work center and no phases); it is
	 * normalised to "" here so the field stays a string — treat "" as "none".
	 */
	workstation: string;
	/** The PLANNED window. Steps can span shifts/days (endTime well past startTime). */
	startTime: string;
	endTime?: string;
	shiftId?: string;
	laborStandardId?: number;
	laborStandardName?: string;
	/**
	 * Which operation of the standard this step is. Null = every operation of
	 * the standard that runs on this work center (Peters' setup / production /
	 * washup on one line).
	 */
	operationId?: number | null;
	/** The batch header this step belongs to, when it is part of a batch. */
	parentWorkOrderId?: number | null;
	/** Customer's batch / process order / job order number. */
	batchNumber?: string | null;
	/** Plant; allocation and the roster filter on it. */
	locationId?: number | null;
	/** JSON object of configurable attribute values keyed by attrKey */
	attributeValues?: string;
	/**
	 * null/undefined/"production" = normal production order;
	 * "cleaning" | "changeover" | "setup" | "maintenance" | "inspection"
	 * render as non-production blocks on the plan.
	 */
	orderType?: string;
	/** Campaign = sequenced series of orders (e.g. batch 4 of 9) */
	campaignId?: number;
	/** 1-based position of this order within its campaign */
	campaignSeq?: number;
	createdDate: string;
	modifiedDate?: string;
}

/** True for a batch header: a parent order with no work center of its own. */
export const isBatchHeader = (order: Pick<WorkOrder, "workstation">): boolean =>
	!order.workstation;

/** Non-production block types recognized by the planner UI */
export const NON_PRODUCTION_ORDER_TYPES = [
	"cleaning",
	"changeover",
	"setup",
	"maintenance",
	"inspection",
] as const;

export type NonProductionOrderType = typeof NON_PRODUCTION_ORDER_TYPES[number];

/** True when the order renders as a non-production block (cleaning, changeover, …) */
export const isNonProductionOrder = (
	orderType?: string | null
): orderType is NonProductionOrderType =>
	Boolean(orderType) &&
	(NON_PRODUCTION_ORDER_TYPES as readonly string[]).includes(orderType as string);

/** @deprecated Use WorkOrder instead */
export type ManufacturingOrder = WorkOrder;

export interface CreateWorkOrderDto {
	productId?: number;
	sku?: string;
	quantity: number;
	/** Empty for a batch header (no work center of its own). */
	workstation: string;
	startTime: string;
	endTime?: string;
	shiftId?: string;
	laborStandardId?: number;
	operationId?: number | null;
	parentWorkOrderId?: number | null;
	batchNumber?: string | null;
	locationId?: number | null;
	/** JSON object of configurable attribute values keyed by attrKey */
	attributeValues?: string;
	/** null/"production" = normal; cleaning/changeover/setup/maintenance/inspection = block */
	orderType?: string;
	campaignId?: number;
	campaignSeq?: number;
}

/** @deprecated Use CreateWorkOrderDto instead */
export type CreateManufacturingOrderDto = CreateWorkOrderDto;

export interface UpdateWorkOrderDto {
	productId?: number;
	sku?: string;
	quantity?: number;
	workstation?: string;
	startTime?: string;
	endTime?: string;
	shiftId?: string;
	laborStandardId?: number;
	operationId?: number | null;
	parentWorkOrderId?: number | null;
	batchNumber?: string | null;
	locationId?: number | null;
	/** JSON object of configurable attribute values keyed by attrKey */
	attributeValues?: string;
	/** null/"production" = normal; cleaning/changeover/setup/maintenance/inspection = block */
	orderType?: string;
	campaignId?: number;
	campaignSeq?: number;
}

/** @deprecated Use UpdateWorkOrderDto instead */
export type UpdateManufacturingOrderDto = UpdateWorkOrderDto;

/**
 * Helper function to format duration in minutes to human readable string
 */
export const formatDuration = (minutes: number): string => {
	if (minutes < 60) {
		return `${Math.round(minutes)}m`;
	}
	const hours = Math.floor(minutes / 60);
	const mins = Math.round(minutes % 60);
	return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

/**
 * Calculate the difference in minutes between two dates
 */
export const getDurationMinutes = (startTime: string, endTime: string): number => {
	const start = new Date(startTime);
	const end = new Date(endTime);
	return (end.getTime() - start.getTime()) / (1000 * 60);
};
